import { useTranslation } from '@rhino-project/core/i18n';
import { useMemo } from 'react';

import {
  useGlobalComponentForModel,
  useOverrides
} from '@rhino-project/core/hooks';
import { getOwnedModels } from '@rhino-project/core/utils';
import { RhinoResource } from '@rhino-project/core';
import { Typography } from '@heroui/react';
import { ModelIndexBase } from './ModelIndex';
import { useModelShowContext } from '@rhino-project/core/hooks';

// Get models owned by this, but filter out models that match properties on the
// models, those are directly displayable (1:many would be nested)
const getRelatedModels = (model: RhinoResource) =>
  getOwnedModels(model).filter((m) => {
    const property =
      model.properties?.[m.model] || model.properties?.[m.modelPlural];

    return (property && property?.readOnly) || !property;
  });

const defaultComponents = {
  ModelIndex: ModelIndexBase
};

export interface ModelShowRelatedProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  getRelatedModels?: (model: RhinoResource) => RhinoResource[];
  [key: string]: unknown;
}

export const ModelShowRelatedBase = ({
  overrides,
  getRelatedModels: getRelatedModelsProp = getRelatedModels
}: ModelShowRelatedProps) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @eslint-react/static-components
  const { ModelIndex } = useOverrides(
    defaultComponents,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    overrides as Record<string, any>
  );
  const { model, resource } = useModelShowContext();

  const indexableModels = useMemo(
    () => getRelatedModelsProp(model),
    [getRelatedModelsProp, model]
  );

  return (
    <div>
      {indexableModels.map((relatedModel) => (
        <div key={relatedModel.name}>
          <Typography.Heading level={4}>
            {t(`rhino.generated.${relatedModel.model}.other`, {
              defaultValue: relatedModel.pluralReadableName
            })}
          </Typography.Heading>
          {/* eslint-disable-next-line @eslint-react/static-components */}
          <ModelIndex
            model={relatedModel}
            parentId={resource?.id}
            defaultFilter={{ [model.model + '_id']: resource?.id }}
            syncUrl={false}
          />
        </div>
      ))}
    </div>
  );
};

export const ModelShowRelated = (props: ModelShowRelatedProps) =>
  useGlobalComponentForModel('ModelShowRelated', ModelShowRelatedBase, props);
